<template>
  <section class="lead-page">
    <div class="page-header">
      <div>
        <h2>线索表</h2>
        <p>查看前台运价查询命中港口预警后生成的线索记录。</p>
      </div>
    </div>

    <div class="toolbar-card">
      <div class="toolbar-grid">
        <el-input
          v-model="filters.keyword"
          clearable
          placeholder="搜索线索ID / 用户名 / 邮箱 / 手机号 / 港口"
          @keyup.enter="searchList"
        />
        <el-select v-model="filters.warningPair" clearable filterable placeholder="预警港口组合">
          <el-option v-for="item in warningPairOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <div class="switch-filter">
          <span>只看预警组合</span>
          <el-switch
            v-model="filters.onlyWarningPair"
            inline-prompt
            active-text="是"
            inactive-text="否"
            @change="searchList"
          />
        </div>
        <!-- <el-select v-model="filters.leadStatus" clearable placeholder="线索状态">
          <el-option v-for="item in leadStatusOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select> -->
        <el-date-picker
          v-model="filters.dateRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          value-format="x"
        />
      </div>
      <div class="toolbar-actions">
        <el-button type="primary" @click="searchList">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
        <el-button :loading="exporting" @click="exportRows">导出</el-button>
      </div>
    </div>

    <div class="table-card">
      <el-table v-loading="loading" :data="list" border>
        <el-table-column prop="leadId" label="线索ID" width="130" />
        <el-table-column prop="warningPortDisplay" label="预警港口组合" min-width="220" show-overflow-tooltip>
          <template #default="{ row }">{{ row.warningPortDisplay || '-' }}</template>
        </el-table-column>
        <el-table-column label="查询时间" min-width="170">
          <template #default="{ row }">{{ formatDateTime(row.queryTime) }}</template>
        </el-table-column>
        <el-table-column prop="userName" label="用户名" min-width="160" show-overflow-tooltip>
          <template #default="{ row }">{{ row.userName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="email" label="联系邮箱" min-width="220" show-overflow-tooltip>
          <template #default="{ row }">{{ row.email || '-' }}</template>
        </el-table-column>
        <el-table-column prop="phone" label="联系电话" min-width="140">
          <template #default="{ row }">{{ row.phone || '-' }}</template>
        </el-table-column>
        <!-- <el-table-column label="线索状态" width="110">
          <template #default="{ row }">
            <el-tag :type="leadStatusTagType(row.leadStatus)">{{ row.leadStatusDesc || '-' }}</el-tag>
          </template>
        </el-table-column> -->
      </el-table>
    </div>

    <Pagination :page="page" :size="size" :total="total" label="线索" @current-change="handlePageChange" />
  </section>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import * as Request from '@/assets/request'
import Pagination from '@/components/spagination'

// const leadStatusOptions = [
//   { label: '未跟进', value: 0 },
//   { label: '跟进中', value: 1 },
//   { label: '已跟进', value: 2 },
// ]

const filters = reactive({
  keyword: '',
  warningPair: null,
  onlyWarningPair: true,
  leadStatus: null,
  dateRange: [],
})

const list = ref([])
const loading = ref(false)
const exporting = ref(false)
const page = ref(1)
const size = ref(10)
const total = ref(0)
const warningPairOptions = ref([])

const formatDateTime = (time) => {
  if (!time) {
    return '-'
  }
  const date = new Date(Number(time))
  if (Number.isNaN(date.getTime())) {
    return '-'
  }
  const pad = (value) => `${value}`.padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// const leadStatusTagType = (status) => {
//   if (status === 1) return 'warning'
//   if (status === 2) return 'success'
//   return 'info'
// }

const getSelectedPair = () => {
  return warningPairOptions.value.find(item => item.value === filters.warningPair) || null
}

const buildQueryPayload = ({ includePagination = true } = {}) => {
  const [startTime, endTime] = filters.dateRange || []
  const selectedPair = getSelectedPair()
  return {
    keyword: filters.keyword?.trim() || undefined,
    leadStatus: filters.leadStatus ?? undefined,
    onlyWarningPair: filters.onlyWarningPair,
    polCode: selectedPair?.polCode || undefined,
    podCode: selectedPair?.podCode || undefined,
    startTime: startTime ? Number(startTime) : undefined,
    endTime: endTime ? Number(endTime) : undefined,
    ...(includePagination ? { page: page.value, size: size.value } : {}),
  }
}

const parseFileName = (response, fallback) => {
  const header = response?.headers?.['content-disposition'] || ''
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1])
  }
  const normalMatch = header.match(/filename="?([^"]+)"?/i)
  if (normalMatch?.[1]) {
    return decodeURIComponent(normalMatch[1])
  }
  return fallback
}

const triggerDownload = (response, fallbackName) => {
  const blob = new Blob([response.data], { type: response.data?.type || 'application/octet-stream' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = parseFileName(response, fallbackName)
  link.click()
  URL.revokeObjectURL(link.href)
}

const fetchWarningOptions = async () => {
  const result = await Request.post({
    url: '/company/lead/warning/detail',
    data: {},
  })
  if (result?.status === 200) {
    warningPairOptions.value = (result.data?.portConfigs || []).map(item => ({
      value: `${item.polCode}_${item.podCode}`,
      label: `${item.polName || item.polCode} -> ${item.podName || item.podCode}`,
      polCode: item.polCode,
      podCode: item.podCode,
    }))
  }
}

const fetchList = async () => {
  loading.value = true
  const result = await Request.post({
    url: '/company/lead/query',
    data: buildQueryPayload(),
  })
  loading.value = false
  if (result?.status === 200) {
    list.value = result.data.list || []
    total.value = result.data.total || 0
  }
}

const exportRows = async () => {
  exporting.value = true
  const response = await Request.download({
    url: '/company/lead/export',
    data: buildQueryPayload({ includePagination: false }),
  })
  exporting.value = false
  if (!response) {
    return
  }
  triggerDownload(response, `线索列表-${Date.now()}.xlsx`)
  ElMessage.success('线索数据导出完成')
}

const searchList = () => {
  page.value = 1
  fetchList()
}

const resetFilters = () => {
  filters.keyword = ''
  filters.warningPair = null
  filters.onlyWarningPair = true
  filters.leadStatus = null
  filters.dateRange = []
  searchList()
}

const handlePageChange = (current) => {
  page.value = current
  fetchList()
}

onMounted(async () => {
  await fetchWarningOptions()
  fetchList()
})
</script>

<style scoped lang="less">
.lead-page {
  .page-header {
    margin-bottom: 20px;

    h2 {
      margin-bottom: 4px;
      font-size: 20px;
      line-height: 28px;
      color: #1f2937;
    }

    p {
      font-size: 13px;
      color: #6b7280;
    }
  }

  .toolbar-card,
  .table-card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  }

  .toolbar-card {
    padding: 12px 16px;
    margin-bottom: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
  }

  .toolbar-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    flex: 1;

    > * {
      flex: 1;
      min-width: 200px;
    }

    .switch-filter {
      flex: 0 0 auto;
      min-width: 140px;
    }

    :deep(.el-input),
    :deep(.el-select),
    :deep(.el-date-editor) {
      width: 100%;
    }

    :deep(.el-date-editor--datetimerange.el-input__wrapper) {
      width: 100%;
      min-width: 320px;
    }
  }

  .toolbar-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
  }

  .switch-filter {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 32px;
    padding: 0 12px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    color: #606266;
    font-size: 14px;
    line-height: 32px;

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .table-card {
    padding: 12px;
  }

  @media (max-width: 900px) {
    .toolbar-card {
      flex-direction: column;
      align-items: stretch;
    }

    .toolbar-grid {
      flex-direction: column;
      align-items: stretch;

      > * {
        min-width: 100%;
      }

      .switch-filter {
        min-width: 100%;
      }

      :deep(.el-date-editor--datetimerange.el-input__wrapper) {
        min-width: 100%;
      }
    }

    .toolbar-actions {
      justify-content: flex-start;
      margin-top: 4px;
    }
  }
}
</style>
